import { createApi } from "@reduxjs/toolkit/query/react";
import { baseCommunicationQueryWithMsalAuth } from "./baseApi";
import { downloadBlob, getFileNameFromHeader } from "@shared/helpers/fileHelper";
import {
  CallbackExportRequest,
  CallbackLogFilter,
  CallbackLogResponse,
  CallbackReportingFilter,
  CallbackReportingResponse,
  CallbackStatus,
} from "@shared/models/callback";
import { GetAskedQuestionsResponse } from "@shared/models/dashboards/GetAskedQuestionsResponse";
import { DashboardFilterRequest } from "@shared/models/dashboards/DashboardFilterRequest";
import { GetOrganisationUsersResponse } from "@shared/models/dashboards/GetOrganisationUsersResponse";
import { GetAverageQuestionsResponse } from "@shared/models/dashboards/GetAverageQuestionsResponse";
import { GetTotalTransactionsResponse } from "@shared/models/dashboards/GetTotalTransactionsResponse";
import { GetInsightUsageResponse } from "@shared/models/dashboards/GetInsightUsageResponse";
import { GetBehaviouralUsageResponse } from "@shared/models/dashboards/GetBehaviouralUsageResponse";
import { GetFeedbackRatingsResponse } from "@shared/models/dashboards/GetFeedbackRatingsResponse";
import { GetUserBehavioursResponse } from "@shared/models/dashboards/GetUserBehavioursResponse";
import { GetAverageDocumentsResponse } from "@shared/models/dashboards/GetAverageDocumentsResponse";
import { ExportDashboardDataFilterRequest } from "@shared/models/dashboards/ExportDashboardDataFilterRequest";
import { CustomerCreditDashboardResponse } from './customerCreditDashboard';

const controllerName = 'dashboard';
const callbackControllerName = 'callback';

interface CustomerCallbackRequestsApiRequest {
  organisation_id?: string;
  start_date?: string | null;
  end_date?: string | null;
  insight_ids?: string[];
  statuses?: CallbackStatus[];
  page_number?: number;
  page_size?: number;
}

interface CustomerCallbackRequestApiItem {
  callback_request_id: string;
  chat_id: string;
  insight_id: string;
  insight_name: string;
  full_name?: string | null;
  mobile?: string | null;
  requester_email?: string | null;
  request_message?: string | null;
  include_chat_history: boolean;
  status: CallbackStatus;
  submitted_on: string;
  sent_on?: string | null;
}

interface CustomerCallbackRequestsApiResponse {
  current_page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  callback_requests: CustomerCallbackRequestApiItem[];
}

interface CustomerCallbackReportingApiResponse {
  insights: Array<{
    name: string;
    count: number;
  }>;
}

export const dashbordsApi = createApi({
  reducerPath: 'dashboardsApi',
  baseQuery: baseCommunicationQueryWithMsalAuth,
  endpoints: (builder) => ({
    getOrganisationUsers: builder.query<GetOrganisationUsersResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/organisation-users`,
        method: "POST",
        body: request,
      })
    }),
    getAskedQuestions: builder.query<GetAskedQuestionsResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/asked-questions`,
        method: "POST",
        body: request,
      }),
    }),
    getAverageQuestions: builder.query<GetAverageQuestionsResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/average-questions`,
        method: "POST",
        body: request,
      }),
    }),
    getTotalTransactions: builder.query<GetTotalTransactionsResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/transactions-count`,
        method: "POST",
        body: request,
      }),
    }),
    getInsightsUsage: builder.query<GetInsightUsageResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/insight-usage`,
        method: "POST",
        body: request,
      }),
    }),
    getBehaviouralUsage: builder.query<GetBehaviouralUsageResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/behavioural-usage`,
        method: "POST",
        body: request,
      }),
    }),
    getFeedbackRatings: builder.query<GetFeedbackRatingsResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/feedback-ratings`,
        method: "POST",
        body: request,
      }),
    }),
    getUserBehaviours: builder.query<GetUserBehavioursResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/user-behaviours`,
        method: "POST",
        body: request,
      }),
    }),
    getAverageDocuments: builder.query<GetAverageDocumentsResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/average-documents`,
        method: "POST",
        body: request,
      }),
    }),
    getCustomerCreditDashboard: builder.query<CustomerCreditDashboardResponse, DashboardFilterRequest>({
      query: (request: DashboardFilterRequest) => ({
        url: `${controllerName}/customer/credit-consumption`,
        method: 'POST',
        body: request,
      }),
    }),
    getCallbackLogs: builder.query<CallbackLogResponse, CallbackLogFilter>({
      transformResponse: (response: CustomerCallbackRequestsApiResponse, _meta, request) => {
        return {
          current_page: response.current_page,
          page_size: response.page_size,
          total_items: response.total_items,
          total_pages: response.total_pages,
          results: response.callback_requests.map((item) => ({
            id: item.callback_request_id,
            organisation_id: request.organisation_ids?.[0] ?? '',
            organisation_name: '',
            insight_id: item.insight_id,
            insight_name: item.insight_name,
            correlation_id: '',
            status: item.status,
            full_name: item.full_name ?? '',
            mobile: item.mobile ?? '',
            email: item.requester_email ?? '',
            message: item.request_message ?? '',
            callback_email: '',
            cc_email: null,
            include_chat_history: item.include_chat_history,
            chat_id: item.chat_id,
            created_on: item.submitted_on,
            queued_on: item.status === CallbackStatus.Queued ? item.submitted_on : null,
            sent_on: item.sent_on ?? null,
            failed_on: null,
            error_message: null,
          })),
        };
      },
      query: (request: CallbackLogFilter) => ({
        url: `${callbackControllerName}/customer/requests`,
        method: "POST",
        body: {
          organisation_id: request.organisation_ids?.[0],
          start_date: request.date_from,
          end_date: request.date_to,
          insight_ids: request.knowledgebase_ids,
          statuses: request.statuses,
          page_number: request.page_number,
          page_size: request.page_size,
        } satisfies CustomerCallbackRequestsApiRequest,
      }),
    }),
    getCallbackReporting: builder.query<CallbackReportingResponse, CallbackReportingFilter>({
      transformResponse: (response: CustomerCallbackReportingApiResponse) => ({
        total: response.insights.reduce((sum, item) => sum + item.count, 0),
        items: response.insights.map((item) => ({
          name: item.name,
          insight_id: item.name,
          insight_name: item.name,
          count: item.count,
        })),
      }),
      query: (request: CallbackReportingFilter) => ({
        url: `${callbackControllerName}/customer/pie-report`,
        method: "POST",
        body: {
          organisation_id: request.organisation_ids[0],
          start_date: request.date_from,
          end_date: request.date_to,
          statuses: request.statuses,
          group_by: request.group_by,
        },
      }),
    }),
    exportQA: builder.mutation<{ ok: true }, ExportDashboardDataFilterRequest>({
      async queryFn(request, api, extra, baseCommunicationQueryWithMsalAuth) {
        const result = await baseCommunicationQueryWithMsalAuth({
          url: `${controllerName}/export-qa`,
          method: "POST",
          body: request,
          headers: {
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseHandler: (res) => res.blob(),
        });

        if (result.error) return { error: result.error };

        const response = (result.meta as any)?.response as Response | undefined;
        const cd = response?.headers?.get("content-disposition") ?? null;

        const filename = getFileNameFromHeader(cd);

        downloadBlob(result.data as Blob, filename);

        return { data: { ok: true } };
      },
    }),
    exportCallbackRequests: builder.mutation<{ ok: true }, CallbackExportRequest>({
      async queryFn(request, api, extra, baseCommunicationQueryWithMsalAuth) {
        const result = await baseCommunicationQueryWithMsalAuth({
          url: `${callbackControllerName}/customer/export`,
          method: "POST",
          body: {
            organisation_id: request.organisation_ids?.[0],
            start_date: request.date_from,
            end_date: request.date_to,
            insight_ids: request.knowledgebase_ids,
            statuses: request.statuses,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          headers: {
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseHandler: (res) => res.blob(),
        });

        if (result.error) return { error: result.error };

        const response = (result.meta as any)?.response as Response | undefined;
        const cd = response?.headers?.get("content-disposition") ?? null;

        const filename = getFileNameFromHeader(cd);

        downloadBlob(result.data as Blob, filename);

        return { data: { ok: true } };
      },
    })
  })
});

export const {
  useGetAskedQuestionsQuery,
  useLazyGetAskedQuestionsQuery,
  useGetAverageQuestionsQuery,
  useLazyGetAverageQuestionsQuery,
  useGetOrganisationUsersQuery,
  useLazyGetOrganisationUsersQuery,
  useGetTotalTransactionsQuery,
  useLazyGetTotalTransactionsQuery,
  useGetInsightsUsageQuery,
  useLazyGetInsightsUsageQuery,
  useGetBehaviouralUsageQuery,
  useLazyGetBehaviouralUsageQuery,
  useExportQAMutation,
  useGetFeedbackRatingsQuery,
  useGetUserBehavioursQuery,
  useLazyGetFeedbackRatingsQuery,
  useLazyGetUserBehavioursQuery,
  useGetAverageDocumentsQuery,
  useLazyGetAverageDocumentsQuery,
  useGetCustomerCreditDashboardQuery,
  useGetCallbackLogsQuery,
  useLazyGetCallbackLogsQuery,
  useGetCallbackReportingQuery,
  useLazyGetCallbackReportingQuery,
  useExportCallbackRequestsMutation,
} = dashbordsApi;
